import React, { useEffect, useMemo, useState } from 'react'
import { RoleLiteDto } from '../../api/role/models/RoleDto'
import personApi from '../../api/person/PersonApi'
import Cards from '../Cards'
import Card from '../Card'
import { PersonLiteDto } from '../../api/person/models/PersonDto'
import { MapExtra } from '../../utils/MapExtra'
import { SetExtra } from '../../utils/SetExtra'
import { formatRelativeTime } from '../../utils/dateUtils'
import Button from '../Button'
import Section from '../Section'
import { PersonUiMicroList } from '../PersonUiMicroList'
import helixSearchV2Api from '../../api/helixSearchV2/HelixSearchV2Api'
import { HelixSearchSessionV2Summary } from '../../api/helixSearchV2/models/HelixSearchSessions'
import HelixSearchV2Insights from '../../uiV2/helixSearchV2Copy/HelixSearchV2Insights'
import { Pages } from '../../Pages'
import {
    ExcludeCompanyBadge,
    ExcludeJobTitleBadge,
    IncludeCompanyBadge,
    IncludeJobTitleBadge,
    KeywordGroupBadge,
    LocationBadge,
    SeniorityBadge,
} from '../../uiV2/helixSearchV2Copy/searchFilters/FilterBadges'
import Badge from '../../uiV2/common/badge/Badge'
import { BadgeColor } from '../../uiV2/common/badge/badgeTypes'
import { HelixSearchFilters } from '../../api/helixSearchV2/models/HelixSearchFilters'
import { HelixSearchLogStatusV2 } from '../../api/helixSearchV2/models/StartHelixSearchV2'

const buildSessionFilterBadges = (
    filters: HelixSearchFilters | null,
): React.ReactNode[] => {
    if (!filters) {
        return []
    }

    const badges: React.ReactNode[] = []

    const pushTextBadges = (
        values: (string | null | undefined)[] | undefined,
        BadgeComponent: React.ComponentType<{ text: string }>,
        prefix: string,
    ) => {
        values?.forEach((value, index) => {
            const trimmed = value?.trim()
            if (!trimmed) {
                return
            }
            badges.push(
                <BadgeComponent
                    key={`${prefix}-${trimmed}-${index}`}
                    text={trimmed}
                />,
            )
        })
    }

    pushTextBadges(
        filters.jobTitlesInclude,
        IncludeJobTitleBadge,
        'job-title-include',
    )
    pushTextBadges(
        filters.jobTitlesExclude,
        ExcludeJobTitleBadge,
        'job-title-exclude',
    )
    pushTextBadges(filters.locations, LocationBadge, 'location')
    pushTextBadges(
        filters.companiesInclude,
        IncludeCompanyBadge,
        'company-include',
    )
    pushTextBadges(
        filters.companiesExclude,
        ExcludeCompanyBadge,
        'company-exclude',
    )
    pushTextBadges(filters.seniorities, SeniorityBadge, 'seniority')

    filters.keywordGroups?.forEach((group, index) => {
        const keywords = group.keywords
            .map((keyword) => keyword.trim())
            .filter(Boolean)
        if (keywords.length === 0) {
            return
        }
        badges.push(
            <KeywordGroupBadge
                key={`keyword-group-${index}-${keywords.join('-')}`}
                text={keywords.join(' OR ')}
            />,
        )
    })

    filters.departments?.forEach((department, index) => {
        const trimmed = department?.trim()
        if (!trimmed) {
            return
        }
        badges.push(
            <Badge
                key={`department-${trimmed}-${index}`}
                color={BadgeColor.Violet}
                className="text-sm"
                borderless={true}
            >
                {trimmed}
            </Badge>,
        )
    })

    return badges
}

const formatStatusLabel = (status: HelixSearchLogStatusV2 | null): string => {
    if (!status) {
        return 'Unknown'
    }

    return status
        .split('_')
        .filter(Boolean)
        .map(
            (word) =>
                `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`,
        )
        .join(' ')
}

const getStatusBadgeColor = (
    status: HelixSearchLogStatusV2 | null,
): BadgeColor => {
    switch (status) {
        case 'COMPLETE':
            return BadgeColor.Success
        case 'FAILED':
        case 'CANCELED':
            return BadgeColor.Error
        case 'CANCELLING':
            return BadgeColor.WarningAmber
        case 'QUEUED':
        case 'DISPATCH_PENDING':
        case 'STARTED':
            return BadgeColor.Progress
        default:
            return BadgeColor.InfoBlue
    }
}

interface Props {
    roleLite: RoleLiteDto
}

export default function RoleRightPane({ roleLite }: Props): React.JSX.Element {
    const [sessions, setSessions] = useState<HelixSearchSessionV2Summary[]>([])
    const [personCache, setPersonCache] = useState<MapExtra<
        string,
        PersonLiteDto
    > | null>(null)

    // const [editModal, setEditModal] = useState<{
    //     index: number
    //     name: string
    // } | null>(null)

    // const [nameError, setNameError] = useState<string | null>(null)
    // const [isUpdating, setIsUpdating] = useState(false)

    useEffect(() => {
        async function loadSessions() {
            try {
                const res = await helixSearchV2Api.getSessionsForRole({
                    roleId: roleLite.id,
                })
                setSessions(res.sessionSummaries)
                const personIds = new SetExtra<string>(
                    res.sessionSummaries.flatMap(
                        (session) => session.networkSeedPersonIds ?? [],
                    ),
                )

                const personLites = (
                    await personApi.getPersonsLite({
                        personIds: personIds.getValuesAsArray(),
                    })
                ).persons

                const newPersonCache = new MapExtra<string, PersonLiteDto>()
                for (const person of personLites) {
                    newPersonCache.set(person.id, person)
                }
                setPersonCache(newPersonCache)
            } catch (err) {
                console.error('Failed to load search sessions:', err)
            }
        }
        loadSessions()
    }, [roleLite.id])

    const earliestSessionTimestamp = useMemo(() => {
        let minTimestamp: number | null = null
        for (const session of sessions) {
            const createdAt = session.createdAt
            if (!createdAt) {
                continue
            }
            const timestamp = new Date(createdAt).getTime()
            if (!Number.isFinite(timestamp)) {
                continue
            }
            if (minTimestamp === null || timestamp < minTimestamp) {
                minTimestamp = timestamp
            }
        }
        return minTimestamp
    }, [sessions])

    const displayedSessions = useMemo(() => {
        if (sessions.length === 0) {
            return []
        }

        return sessions.filter((session) => {
            const createdAt = session.createdAt
            if (!createdAt || earliestSessionTimestamp === null) {
                return true
            }
            const timestamp = new Date(createdAt).getTime()
            if (!Number.isFinite(timestamp)) {
                return true
            }

            const isFirstSession = timestamp === earliestSessionTimestamp
            const hidePersistedFilters =
                session.logStatus === 'CANCELED' && isFirstSession

            return !hidePersistedFilters
        })
    }, [sessions, earliestSessionTimestamp])

    // const handleRenameSubmit = async () => {
    //     if (!editModal) return

    //     const { index, name } = editModal

    //     if (name.trim() === '') {
    //         setNameError('Session name cannot be empty.')
    //         return
    //     }

    //     try {
    //         setIsUpdating(true)
    //         await helixSearchV2Api.updateSessionName({
    //             sst: sessions[index].sst,
    //             name,
    //         })

    //         const updated = [...sessions]
    //         updated[index].name = name
    //         setSessions(updated)
    //         setEditModal(null)
    //         setNameError(null)
    //     } catch (err) {
    //         console.error('Failed to update session name:', err)
    //         setNameError('Failed to save name. Please try again.')
    //     } finally {
    //         setIsUpdating(false)
    //     }
    // }

    // const handleDeleteSearchSession = async (index: number) => {
    //     const session = sessions[index]
    //     try {
    //         setIsUpdating(true)
    //         await helixSearchV2Api.deleteSession({
    //             sst: session.sst,
    //         })

    //         const updatedSessions = sessions.filter((_, i) => i !== index)
    //         setSessions(updatedSessions)
    //     } catch (err) {
    //         console.error('Failed to delete session:', err)
    //     } finally {
    //         setIsUpdating(false)
    //     }
    // }

    if (!personCache) {
        return <div className="text-center">Loading...</div>
    }

    return (
        <Section title="Recent Searches">
            {displayedSessions.length > 0 ? (
                <Cards className="rounded-lg mt-2" rowHeight={140}>
                    {displayedSessions.map((session) => {
                        const createdAtRelative = formatRelativeTime(
                            session.createdAt,
                        )
                        const filterBadges = buildSessionFilterBadges(
                            session.searchFilters,
                        )

                        return (
                            <Card
                                key={session.sst}
                                showTopSection={false}
                                classes="truncate w-full bg-white text-[12px] py-2 px-3"
                            >
                                <div className="my-2 flex flex-wrap gap-2">
                                    <Badge
                                        color={getStatusBadgeColor(
                                            session.logStatus,
                                        )}
                                        className="text-xs uppercase tracking-wider"
                                        borderless={true}
                                    >
                                        {formatStatusLabel(session.logStatus)}
                                    </Badge>
                                </div>
                                <div className="flex items-center mb-3 mt-1 justify-between">
                                    {/* Left: network seed links */}
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {session.networkSeedPersonIds && (
                                            <div className="-mb-2">
                                                <PersonUiMicroList
                                                    ids={
                                                        session.networkSeedPersonIds
                                                    }
                                                    personCache={personCache}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Right: View Search button */}
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <Button
                                            style="secondary"
                                            to={`${Pages.HelixSearchV2}?helixV2Sst=${encodeURIComponent(
                                                session.sst,
                                            )}&roleId=${encodeURIComponent(
                                                roleLite.id,
                                            )}`}
                                            type="link"
                                            size="small"
                                        >
                                            View Search
                                        </Button>
                                    </div>
                                </div>
                                {/* <div className="flex -space-x-2 flex-wrap mb-2">
                                    {session.networkSeedPersonIds?.map(
                                        (seedPersonId) => (
                                            <PersonSmallPic
                                                key={seedPersonId}
                                                person={personCache.getOrThrow(
                                                    seedPersonId,
                                                )}
                                                size="32px"
                                            />
                                        ),
                                    )}
                                </div> */}
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {filterBadges.length > 0 ? (
                                        filterBadges
                                    ) : (
                                        <Badge
                                            color={BadgeColor.Neutral2}
                                            className="text-sm"
                                            borderless={true}
                                        >
                                            No filters saved
                                        </Badge>
                                    )}
                                </div>
                                <div className="my-2">
                                    <HelixSearchV2Insights
                                        inNetwork={
                                            session.networkPersonCount ?? 0
                                        }
                                        matchesBoth={
                                            session.combinedResultCount ?? 0
                                        }
                                        filteredCandidatePoolCount={
                                            session.combinedResultCount ?? 0
                                        }
                                        topPickCount={session.topPickCount ?? 0}
                                        enabledLlmScoring={
                                            session.enableLlmScoring ?? false
                                        }
                                        logStatus={session.logStatus ?? null}
                                        size="micro"
                                    />
                                </div>
                                <div className="flex items-center justify-end mt-3">
                                    <div className="text-xs text-gray-500 whitespace-nowrap">
                                        {createdAtRelative}
                                    </div>
                                </div>
                            </Card>
                        )
                    })}
                </Cards>
            ) : (
                <p className="italic text-gray-500 mt-4">No searches yet.</p>
            )}
            {/* {editModal && (
                <Modal
                    title="Rename Search Session"
                    onClose={() => setEditModal(null)}
                    size="small"
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                onClick={() => setEditModal(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="primary"
                                onClick={handleRenameSubmit}
                                enabled={!isUpdating}
                            >
                                Save
                            </Button>
                        </>
                    }
                >
                    <input
                        type="text"
                        value={editModal.name}
                        onChange={(e) =>
                            setEditModal((prev) =>
                                prev ? { ...prev, name: e.target.value } : prev,
                            )
                        }
                        className={`w-full border rounded px-3 py-2 ${
                            nameError
                                ? 'border-red-500 bg-red-50 text-red-800'
                                : 'border-gray-300'
                        }`}
                        placeholder="Enter new session name"
                        disabled={isUpdating}
                    />
                    {nameError && (
                        <p className="text-sm text-red-600 mt-2">{nameError}</p>
                    )}
                </Modal>
            )} */}
        </Section>
    )
}
